import { MdClose } from 'react-icons/md';
import { Portal } from '../../components/Modal';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Button } from '@wot/components';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import workshopRegistrationsApi, { IRegistration } from '../../api/workshopRegistrations';

export default function RegistrationDetail() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState<IRegistration | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { registrationId } = useParams();

  useEffect(() => {
    if (registrationId) {
      workshopRegistrationsApi.getById(registrationId).then((data) => {
        setRegistration(data);
      });
    }
  }, [registrationId]);

  const handleClose = () => navigate(-1);

  const handleDelete = async () => {
    if (!registration) return;
    setDeleting(true);
    try {
      await workshopRegistrationsApi.remove(registration._id);
      toast.success('Registration deleted');
      queryClient.invalidateQueries({ queryKey: ['workshop-registrations'] });
      handleClose();
    } catch {
      toast.error('Failed to delete registration');
    } finally {
      setDeleting(false);
    }
  };

  if (!registration) return null;

  return (
    <Portal>
      <div className="relative w-full h-screen flex justify-center -z-10">
        <div onClick={handleClose} className="absolute top-0 left-0 w-full h-full bg-black/70 -z-10" />
        <div className="w-full h-full mx-auto my-auto flex justify-end">
          <div className="w-[40%] h-full flex flex-col gap-5 bg-white rounded-l-2xl p-7 overflow-y-scroll">
            <div className="w-full flex items-center justify-between">
              <h1 className="text-lg font-semibold">Registration {registration._id.slice(-8)}</h1>
              <MdClose className="text-2xl cursor-pointer" onClick={handleClose} />
            </div>

            <div className="flex flex-col gap-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span className="font-medium">{format(parseISO(registration.createdAt), 'd LLL, yyyy h:mm a')}</span>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-base font-semibold mb-3">Student Details</h2>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Student Name</span>
                  <span className="font-medium">{registration.studentName || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Parent Name</span>
                  <span className="font-medium">{registration.parentName || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Email</span>
                  <span className="font-medium">{registration.email || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Phone</span>
                  <span className="font-medium">{registration.phone || '-'}</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h2 className="text-base font-semibold mb-3">Workshop Details</h2>
              <div className="flex flex-col gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Workshop</span>
                  <span className="font-medium">{registration.workshop || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Class</span>
                  <span className="font-medium">{registration.className || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-medium">{registration.paidAmount ? `${registration.currency} ${registration.paidAmount}` : '-'}</span>
                </div>
              </div>
            </div>

            <div className="w-full flex items-center gap-5 mt-auto">
              <Button type="s-outlined" block text="Cancel" onClick={handleClose} />
              <Button type="primary" block text="Delete" loading={deleting} onClick={handleDelete} className="!bg-red-600 !border-red-600 hover:!bg-red-700" />
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
